import {useInfiniteQuery} from '@tanstack/react-query';

import apiService from '../../transport/apiService';

/** Shorter keywords do not query (SearchScreen and the web header search, DIS-1907). */
export const MIN_SEARCH_KEYWORDS = 3;

/**
 * Standalone infinite query for global keyword search.
 *
 * Unlike useSearchLines (which reads from the shared SearchContext used by
 * the shop/department browsing flow), this hook accepts explicit params and
 * has zero coupling to SearchContext.  This eliminates the dispatch race
 * conditions that occurred when both SearchScreen and RangeProductListScreen
 * wrote to the same context.
 */
export function useGlobalSearchLines(storeId: string | null, keywords: string) {
  return useInfiniteQuery({
    queryKey: ['global-search', storeId, 'lines', {keywords: keywords || undefined}],
    queryFn: async ({pageParam = 1}) => {
      const response = await apiService.getDepartmentLines(
        {
          keywords: keywords || undefined,
          page: pageParam,
          page_size: 48,
        },
        // Strip the default `department` header from this request.
        // rangeStore.selectDepartment() sets a department header as an
        // axios default — without {unscoped: true}, global search would
        // inherit the Shop's department scope and return scoped results.
        {unscoped: true},
      );
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage?.meta) return undefined;
      const {page, page_count} = lastPage.meta;
      return page < page_count ? page + 1 : undefined;
    },
    enabled: !!storeId && !!keywords && keywords.length >= MIN_SEARCH_KEYWORDS,
  });
}
